import { useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useSession } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { CalendarPlus, Loader2 } from "lucide-react";

import { useAuthModal } from "@/components/auth/auth-modal-context";
import { BookingRequestFlow } from "@/components/booking-request/booking-request-flow";
import { BookingFlow } from "@/components/booking/booking-flow";
import { ServicesPausedNotice } from "@/components/booking/services-paused-notice";
import type { BookingProvider } from "@/components/booking/types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useNoIndexMeta } from "@/lib/hooks/use-noindex-meta";

interface BookSearch {
  /**
   * `request` forces the booking-request wizard even when the provider looks
   * bookable — used by the "Request an appointment" CTA on the public profile.
   * Otherwise the wizard is chosen from `bookingCapabilities` plus the
   * server-computed `acceptsBookingRequests`.
   */
  mode?: "request";
}

/**
 * Viewport minus the anon layout's sticky header (h-16 + its 1px border +
 * the top safe-area), so the wizard's sticky action bar sits on the real
 * bottom edge instead of 4rem below the fold.
 */
const PAGE_HEIGHT = "h-[calc(100dvh_-_4rem_-_1px_-_env(safe-area-inset-top))]";

/** True when at least one consultation channel can actually take a booking. */
function canBookOnline(provider: BookingProvider): boolean {
  const caps = provider.bookingCapabilities;
  // No capability block (older API shape) → assume online booking works rather
  // than silently downgrading the patient to a request.
  if (!caps) return true;
  return caps.in_office.canAccept || caps.video.canAccept || caps.home_visit.canAccept;
}

/**
 * Dedicated booking entry-point. Hit by the `portal/book/:id` redirect
 * from `apps/web` provider profile and by chip-driven deep links. Guards
 * for auth (opens the auth modal inline) and renders the wizard
 * full-bleed below the app shell. On success the wizard's success
 * screen routes the user to the appointment confirmation page.
 */
export const Route = createFileRoute("/_anon/book/$providerId")({
  validateSearch: (search: Record<string, unknown>): BookSearch => ({
    mode: search.mode === "request" ? "request" : undefined,
  }),
  component: BookProviderRoute,
});

function BookProviderRoute() {
  const { providerId } = Route.useParams();
  const { mode } = Route.useSearch();
  const { status } = useSession();
  const auth = useAuthModal();
  const navigate = useNavigate();
  // Set when the API answers `409 USE_ONLINE_BOOKING` — the provider opened
  // their calendar while the wizard was on screen.
  const [forceOnlineBooking, setForceOnlineBooking] = useState(false);

  useNoIndexMeta();

  // Auth gate — open the auth modal as soon as we know the user is
  // unauthenticated. After login the provider redirects them back here.
  useEffect(() => {
    if (status === "unauthenticated") {
      auth.open({
        intent: "book",
        returnTo:
          typeof window !== "undefined"
            ? window.location.pathname + window.location.search
            : `/book/${providerId}`,
      });
    }
  }, [status, auth, providerId]);

  const query = useQuery<BookingProvider, Error>({
    queryKey: ["booking-provider", providerId],
    queryFn: () => apiFetch.get<BookingProvider>(`/api/providers/${providerId}`),
    enabled: status === "authenticated",
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 404) && failureCount < 2,
  });

  if (status === "loading" || (status === "authenticated" && query.isLoading)) {
    return <BookingPageSkeleton />;
  }

  if (status === "unauthenticated") {
    return <AuthGateView providerId={providerId} />;
  }

  if (query.isError || !query.data) {
    const isNotFound = query.error instanceof ApiError && query.error.status === 404;
    return <ProviderError isNotFound={isNotFound} />;
  }

  // Default close → back to the profile when reachable, else home. The
  // success screens route onward via their own Links before this fires.
  const handleClose = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
    } else {
      void navigate({ to: "/" });
    }
  };

  const provider = query.data;

  // Services paused by the owner: the message, no booking steps.
  if (provider.servicesPaused === true) {
    return (
      <main className="mx-auto flex w-full max-w-md flex-col items-center px-4 py-12">
        <ServicesPausedNotice
          pausedUntil={provider.servicesPausedUntil}
          action={
            <Button
              variant="outline"
              className="mt-3 min-h-[44px] rounded-lg px-6"
              onClick={handleClose}
            >
              <Trans>Back</Trans>
            </Button>
          }
        />
      </main>
    );
  }

  // Only a strict `true` counts — `acceptsBookingRequests` is computed by the
  // API, and a response without it must never downgrade to a request.
  const requestMode =
    !forceOnlineBooking &&
    (mode === "request" || (!canBookOnline(provider) && provider.acceptsBookingRequests === true));

  return (
    // Phone: one full-height card, sticky action bar at the bottom.
    // ≥1024px: steps column + sticky summary column (rendered by the flows).
    <main
      className={cn(
        PAGE_HEIGHT,
        "bg-card border-border mx-auto flex w-full max-w-2xl flex-col sm:border-x lg:max-w-6xl lg:border-x-0 lg:bg-transparent",
      )}
    >
      {requestMode ? (
        <BookingRequestFlow
          provider={provider}
          onClose={handleClose}
          onUseOnlineBooking={() => setForceOnlineBooking(true)}
          layout="page"
        />
      ) : (
        <BookingFlow provider={provider} onClose={handleClose} layout="page" />
      )}
    </main>
  );
}

function BookingPageSkeleton() {
  // Same frame as the loaded page (steps column + desktop summary column), so
  // nothing jumps when the provider arrives. The scan line runs under the
  // header placeholder.
  return (
    <main
      aria-busy="true"
      className={cn(
        PAGE_HEIGHT,
        "mx-auto flex w-full max-w-2xl flex-col lg:max-w-6xl lg:flex-row lg:gap-6 lg:p-6",
      )}
    >
      <div className="lg:border-border lg:bg-card flex min-w-0 flex-1 flex-col gap-3 px-4 py-4 sm:px-6 lg:rounded-2xl lg:border">
        <div className="puls-scan pb-2">
          <Skeleton className="h-12 w-full rounded-xl" />
        </div>
        <Skeleton className="h-8 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="mt-auto h-12 w-full rounded-xl" />
      </div>
      <div className="hidden w-[22rem] shrink-0 flex-col gap-3 lg:flex">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    </main>
  );
}

function AuthGateView({ providerId }: { providerId: string }) {
  const { t } = useLingui();
  const auth = useAuthModal();

  return (
    <main className="mx-auto flex w-full max-w-md flex-col items-center gap-3 px-4 py-12 text-center">
      <span className="bg-primary-soft text-primary grid size-16 place-items-center rounded-full">
        <CalendarPlus className="size-8" aria-hidden />
      </span>
      <h1 className="text-foreground text-xl font-bold">
        <Trans>Sign in to book</Trans>
      </h1>
      <p className="text-muted-foreground text-sm">
        <Trans>
          One-tap sign-in via Google, Apple, email, or phone. We'll bring you back here.
        </Trans>
      </p>
      <Button
        className="mt-4 min-h-[44px] w-full max-w-xs px-6 text-sm font-semibold"
        onClick={() =>
          auth.open({
            intent: "book",
            returnTo: `/book/${providerId}`,
          })
        }
      >
        <Trans>Sign in</Trans>
      </Button>
      <Loader2 className="text-muted-foreground mt-4 size-4 animate-spin" aria-hidden />
      <p className="text-muted-foreground text-xs">
        <Trans>Waiting for sign-in…</Trans>
      </p>
      {/* Defensive deep-link recovery — useful for screen readers */}
      <p className="sr-only">{t`Booking gate for provider ${providerId}`}</p>
    </main>
  );
}

function ProviderError({ isNotFound }: { isNotFound: boolean }) {
  return (
    <main className="mx-auto flex w-full max-w-md flex-col items-center gap-3 px-4 py-12 text-center">
      {/* Radar (Puls empty state) — same as the /find no-results screen */}
      <div aria-hidden className="puls-radar mb-1 size-24 shrink-0" />
      <h1 className="text-foreground text-xl font-bold">
        {isNotFound ? <Trans>Provider not found</Trans> : <Trans>Could not load provider</Trans>}
      </h1>
      <p className="text-muted-foreground text-sm">
        {isNotFound ? (
          <Trans>The link may be outdated. Try searching the directory.</Trans>
        ) : (
          <Trans>Please check your connection and try again.</Trans>
        )}
      </p>
      <Link
        to="/find"
        className="bg-primary text-primary-foreground mt-3 inline-flex min-h-[44px] items-center rounded-lg px-4 py-2 text-sm font-semibold"
      >
        <Trans>Find a provider</Trans>
      </Link>
    </main>
  );
}
